import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReveal } from '../../../hooks/useReveal';
import { supabase, TradeBrand } from '../../../lib/supabase';
import { tradeBrands as mockTradeBrands } from '../../../mocks/importBrands';

function getLocText(obj: Record<string, string> | null | undefined, lang: string, fallback: string): string {
  if (!obj) return fallback;
  return obj[lang] || obj['ko'] || fallback;
}

function getLocArr(obj: Record<string, string[]> | null | undefined, lang: string, fallback: string[]): string[] {
  if (!obj) return fallback;
  return obj[lang] || obj['ko'] || fallback;
}

export default function ServicesPreview() {
  const ref = useReveal();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [brands, setBrands] = useState<TradeBrand[]>(mockTradeBrands as TradeBrand[]);

  useEffect(() => {
    supabase
      .from('trade_brands')
      .select('*')
      .order('sort_order')
      .then(({ data, error }) => {
        if (error) {
          console.warn('[TradeBrands] Supabase error, using mock:', error.message);
        } else if (data && data.length > 0) {
          setBrands(data as TradeBrand[]);
        }
      });
  }, []);

  return (
    <section ref={ref} className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between mb-10 lg:mb-16">
          <div>
            <p className="text-xs font-sans font-semibold tracking-[0.2em] text-navy/40 uppercase mb-4">
              {t('brands.eyebrow')}
            </p>
            <h2 className="font-serif text-navy leading-tight">
              <span className="block text-3xl lg:text-5xl font-bold">{t('brands.heading1')}</span>
            </h2>
          </div>
          <Link
            to="/contact"
            className="flex items-center gap-2 text-sm font-sans text-navy/60 hover:text-navy transition-colors duration-200 cursor-pointer whitespace-nowrap pb-1 border-b border-transparent hover:border-navy self-start sm:self-auto"
          >
            {t('brands.inquiry')} <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Brand Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {brands.map((brand, i) => {
            const isHovered = hoveredId === brand.id;
            const isImport = brand.direction === 'import';

            const localTagline = getLocText(brand.tagline_i18n as Record<string, string> | null, lang, brand.tagline);
            const localCategory = getLocText(brand.category_i18n as Record<string, string> | null, lang, brand.category);
            const localDescription = getLocText(brand.description_i18n as Record<string, string> | null, lang, brand.description);
            const localFeatures = getLocArr(brand.features_i18n as Record<string, string[]> | null, lang, brand.features);

            return (
              <div key={brand.id} className={`reveal stagger-${i + 1}`}>
                <div
                  onMouseEnter={() => setHoveredId(brand.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    height: isHovered ? '660px' : '480px',
                    borderColor: isHovered ? 'rgba(10,22,40,0.2)' : '#E8EAED',
                    transition: 'height 0.5s ease-in-out, border-color 0.3s ease',
                  }}
                  className="overflow-hidden rounded-xl border cursor-pointer"
                >
                  {/* Image */}
                  <div
                    style={{
                      height: isHovered ? '280px' : '300px',
                      transition: 'height 0.5s ease-in-out',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={brand.image_url}
                      alt={brand.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top',
                        transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                        transition: 'transform 0.5s ease-in-out',
                      }}
                    />
                    {/* Direction Badge */}
                    <div className="absolute top-4 left-4">
                      <span
                        className={`px-3 py-1 text-xs font-sans font-semibold rounded-full tracking-wide ${
                          isImport ? 'bg-navy/80 text-white' : 'bg-white/85 text-navy'
                        } backdrop-blur-sm`}
                      >
                        {isImport ? t('brands.import') : t('brands.export')}
                      </span>
                    </div>
                    {/* Flags */}
                    <div className="absolute top-4 right-4 flex items-center gap-1">
                      <span className="text-xl">{brand.origin_flag}</span>
                      <i className="ri-arrow-right-line text-white/80 text-xs" />
                      <span className="text-xl">{brand.destination_flag}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-7">
                    <p className="text-xs font-sans text-navy/40 uppercase tracking-widest mb-2">{localCategory}</p>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${brand.icon} text-white text-lg`} />
                      </div>
                      <div>
                        <h3 className="font-serif text-navy text-xl font-semibold leading-tight">{brand.name}</h3>
                        <p className="text-navy/50 text-xs font-sans mt-0.5">{localTagline}</p>
                      </div>
                    </div>
                    <p className="text-navy/60 text-sm font-sans leading-relaxed">{localDescription}</p>

                    {/* Hover content */}
                    <div
                      style={{
                        opacity: isHovered ? 1 : 0,
                        transform: isHovered ? 'translateY(0px)' : 'translateY(12px)',
                        transition: 'opacity 0.35s ease-in-out 0.18s, transform 0.35s ease-in-out 0.18s',
                        marginTop: '20px',
                        pointerEvents: isHovered ? 'auto' : 'none',
                      }}
                    >
                      <div className="w-8 h-px bg-silver mb-4" />
                      <ul className="space-y-2 mb-5">
                        {localFeatures.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-xs text-navy/55 font-sans">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-navy/40" />
                            </div>
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-navy border-b border-navy pb-0.5 hover:gap-2.5 transition-all duration-200 cursor-pointer"
                      >
                        {t('brands.inquireNow')} <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
